import React from 'react';
import clsx from 'clsx';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import {
  CmsBrowseResultsSummary,
  generateNonTreatmentWhat,
} from 'js/model/view/browse-page/browse-results-summary';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { RequestData } from 'js/helpers/service';
import { TreatmentSearchHeader } from 'js/pages/BrowsePage/WhatWhereWhen/TreatmentSearchHeader';
import { defaultItemName } from 'js/pages/BrowsePage/WhatWhereWhen/TreatmentSearchInput/item-results';
import { urlContext, UrlWithTreatwell } from 'js/components/url';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { ViewModeToggle, ViewMode } from 'js/pages/BrowsePage/ViewModeToggle';
import { capitalize } from 'js/helpers/capitalize';

import styles from './WhatWhereWhen.module.css';

interface Props {
  pageData: RequestData;
  cms: CmsBrowsePage;
  aggregations: Aggregations;
  specification: VenueListingSpecificationOutput;
  isCollapsed: boolean;
  collapse: (collapsed: boolean) => void;
  searchOptions: SearchOptions;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onAnySelectFocus?: () => void;
}

export class WhatWhereWhen extends React.PureComponent<Props> {
  public static contextType = urlContext;

  private headline(
    cms: CmsBrowsePage,
    specification: VenueListingSpecificationOutput
  ): string | null {
    // All headlines should be plural
    const resultCount = 2;
    const { query } = this.context as UrlWithTreatwell;
    return generateNonTreatmentWhat(
      cms as CmsBrowseResultsSummary,
      specification,
      resultCount,
      query ? query.titleoverride : undefined
    );
  }

  public render(): React.ReactNode {
    const {
      pageData,
      cms,
      aggregations,
      specification,
      isCollapsed,
      collapse,
      searchOptions,
      viewMode,
      onViewModeChange,
      onAnySelectFocus,
    } = this.props;
    const cmsViewMode: CmsBrowsePage['page']['browse'] = cms.page.browse;

    const treatmentName = defaultItemName(
      cms.page.browse.filters,
      aggregations,
      specification.treatmentCategories || [],
      specification.treatmentCategoryGroup,
      specification.venueType
    );

    const isBrand = pageData.pageParameters?.brands;
    const headlineContent = this.headline(cms, specification) || treatmentName;
    const headline = isBrand ? headlineContent : capitalize(headlineContent);

    /*
      Always render the inputs regardless of whether the WhWhWh is expanded
      or collapsed. The WhWhWh is initially (server) rendered collapsed
      for a good mobile experience, where it will initially be collapsed
      (and it's desirable to not have a flash of expanded state before collapsing).

      Hidden links are rendered by some of the inputs, so that search engines
      can crawl alternate treatments and locations. If the inputs aren't server
      rendered the crawable links won't be rendered.

      When the WhWhWh is collapsed, the inputs will be rendered hidden.
     */
    return (
      <div className={styles.www}>
        <div className={styles.inputs}>
          <TreatmentSearchHeader
            aggregations={aggregations}
            cms={cms}
            pageData={pageData}
            isCollapsed={isCollapsed}
            specification={specification}
            searchOptions={searchOptions}
            collapse={() => collapse(false)}
            treatmentName={treatmentName}
            onAnySelectFocus={() => {
              if (onAnySelectFocus) {
                onAnySelectFocus();
              }
            }}
          />
        </div>
        <div
          className={clsx(styles.headline, {
            [styles.collapsed]: isCollapsed,
          })}
        >
          {headline}
        </div>

        <div
          className={clsx(styles.viewModeToggle, {
            [styles.collapsed]: isCollapsed,
          })}
        >
          <ViewModeToggle
            cms={cmsViewMode}
            currentMode={viewMode}
            onChange={onViewModeChange}
          />
        </div>
      </div>
    );
  }
}
